import { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { posthog } from 'posthog-js';
import { POSTHOG_EVENTS } from 'constants/posthogEvents';
import { Box, Paper, Typography, IconButton, Fab, Avatar, Button, Tooltip, Link } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { useReport } from '../../context/ReportContext';
import { useLessonPreview } from 'contexts/LessonPreviewContext';
import { useClasses, useSchoolStudents } from 'hooks';
import { WidgetSelectionMenu } from './WidgetSelectionMenu';
import { ChatMessageBubble } from './ChatMessageBubble';
import { InitialSuggestions } from './InitialSuggestions';
import { PiiResolutionCard } from './PiiResolutionCard';
import { ChatInputBar } from './ChatInputBar';
import { useConversation, useChatComposer, useSendMessage, useWidgetActions, useEditActions } from './hooks';
import {
    headerGradient,
    assistantAvatarSx,
    PII_DISCLAIMER,
    PII_DISCLAIMER_LEARN_MORE,
    PII_PROTECTION_HELP_URL,
    AI_DISCLAIMER,
} from './chatConstants';
import '../../styles/variables.css';
import './AIChatbot.css';

export function AIChatbot({ open: openProp, onOpenChange }) {
    const controlled = openProp !== undefined;
    const [internalOpen, setInternalOpen] = useState(false);
    const isOpen = controlled ? openProp : internalOpen;
    const setIsOpen = controlled ? (v) => onOpenChange?.(typeof v === 'function' ? v(openProp) : v) : setInternalOpen;
    const { addWidget, state, updateWidget, updateLayout } = useReport();
    const schoolClasses = useClasses();
    const schoolStudents = useSchoolStudents({ sort: true });
    const { openLessonPreview } = useLessonPreview();
    const userId = useSelector((state) => state.auth?.teacher?.id);
    const profile = useSelector((state) => state.auth?.teacher);
    const schoolId = useSelector(
        (state) =>
            state.auth?.teacher?.schoolId ?? state.auth?.teacher?.user_groups?.schoolId ?? state.auth?.teacher?.groupId,
    );

    const [isTyping, setIsTyping] = useState(false);
    const [error, setError] = useState(null);

    const messagesEndRef = useRef(null);
    const conversationIdRef = useRef(null);
    const messagesRef = useRef([]);
    const inputValueRef = useRef('');
    const setPendingWidgetRef = useRef(null);
    const handleSendMessageRef = useRef(null);
    const exchangeIdRef = useRef(null);

    const conversation = useConversation();
    const { messages, setMessages } = conversation;
    messagesRef.current = messages;

    const composer = useChatComposer({
        isOpen,
        schoolClasses,
        schoolStudents,
    });
    inputValueRef.current = composer.inputValue;

    const widgetActions = useWidgetActions({
        addWidget,
        state,
        messages,
        setMessages,
        setCurrentPhase: conversation.setCurrentPhase,
        setPendingWidgetId: conversation.setPendingWidgetId,
        pendingWidgetIdRef: conversation.pendingWidgetIdRef,
        setError,
        hasEditKeywords: conversation.hasEditKeywords,
        handleSendMessageRef,
    });
    setPendingWidgetRef.current = widgetActions.setPendingWidget;

    const editActions = useEditActions({
        updateWidget,
        updateLayout,
        state,
        messages,
        setMessages,
        setError,
    });

    const { handleSendMessage } = useSendMessage({
        messagesRef,
        setMessages: conversation.setMessages,
        currentPhase: conversation.currentPhase,
        setCurrentPhase: conversation.setCurrentPhase,
        setPendingWidgetId: conversation.setPendingWidgetId,
        pendingWidgetIdRef: conversation.pendingWidgetIdRef,
        getRecentHistory: conversation.getRecentHistory,
        getFullHistory: conversation.getFullHistory,
        setError,
        setPendingWidgetRef,
        setPendingPiiResolution: composer.setPendingPiiResolution,
        setPiiSearchQueries: composer.setPiiSearchQueries,
        setInputValue: composer.setInputValue,
        inputRef: composer.inputRef,
        setIsTyping,
        state,
        conversationIdRef,
        inputValueRef,
        exchangeIdRef,
    });

    handleSendMessageRef.current = handleSendMessage;

    const widgetActionsWithSend = {
        ...widgetActions,
        handleSendMessage,
    };
    // const SCOPE_REF_REGEX = /\{\{(\w+):([^}]+)\}\}/g;

    // /** Same blue gradient as Fae header – use for chatbot, messages, chips, buttons */
    // const headerGradient =
    //     'linear-gradient(135deg, var(--color-primary-dark, #0d47a1) 0%, var(--color-primary, #1976d2) 100%)';

    // /** Assistant (Fae) avatar – gradient circle with robot icon */
    // const assistantAvatarSx = {
    //     width: 32,
    //     height: 32,
    //     flexShrink: 0,
    //     background: headerGradient,
    //     color: 'white',
    // };

    /** Serialize contenteditable DOM to message string with {{type:id}} for scope spans */
    function serializeContentEditable(container) {
        if (!container) return '';
        let out = '';
        const walk = (node) => {
            if (node.nodeType === Node.TEXT_NODE) {
                out += node.textContent;
                return;
            }
            if (node.nodeType === Node.ELEMENT_NODE) {
                const el = node;
                if (el.getAttribute?.('data-entity-type') && el.getAttribute?.('data-entity-id') != null) {
                    out += `{{${el.getAttribute('data-entity-type')}:${el.getAttribute('data-entity-id')}}}`;
                    return;
                }
                el.childNodes.forEach(walk);
            }
        };
        container.childNodes.forEach(walk);
        return out;
    }

    // interface removed
    //   id;
    //   role: 'user' | 'assistant';
    //   content;
    //   timestamp;
    //   widget?;
    //   edit?;
    //   dismissed?;
    //   isAutomaticProgression?; // Flag to hide automatic progression messages in UI
    //   suggestedPrompts?; // Optional suggested prompts from API
    // }

    //     const INITIAL_MESSAGE = {
    //         id: 'initial',
    //         role: 'assistant',
    //         content: `Hello! I'm Fae, your AI assistant. I'm here to help answer questions about your school's data and wellbeing insights.

    // **Please don't include personal or identifiable information** in your messages. Use the **scope selector** below to choose which class or student you're asking about—that keeps your questions focused and your data private.

    // What would you like to know?`,
    //         timestamp: new Date(),
    //     };

    //     const SUGGESTIONS = [
    //         {
    //             text: 'What is the weekly engagement rate for our students?',
    //             prompt: 'What is the weekly engagement rate for our students?',
    //         },
    //         {
    //             text: 'How does student readiness compare this week vs last week?',
    //             prompt: 'How does student readiness compare this week vs last week?',
    //         },
    //         {
    //             text: 'What emotions were the most common this term?',
    //             prompt: 'What emotions were the most common for students to check-in with this term',
    //         },
    //     ];

    // interface removed
    //   // Phase 1: Intent Detection Response
    //   intent?: 'create' | 'edit' | 'clarify';
    //   message;
    //   requiresFullContext?;
    //   requiresWidgetSelection?;
    //   matchingWidgets?: WidgetSummary[];
    //
    //   // Phase 2: Widget Generation/Edit Response
    //   widget?;
    //   layout?: GridItem;
    //   widgets?: Array<{
    //     widget;
    //     layout: GridItem;
    //   }>;
    //   changes?: Record<string, string>;
    //
    //   // Optional suggested prompts (can be Yes/No buttons or follow-up questions)
    //   suggestedPrompts?;
    // }

    // Legacy EditResponse - kept for backward compatibility during migration
    // interface removed
    //   widget;
    //   layout?: GridItem;
    //   message;
    //   changes?: Record<string, string>;
    // }

    // export function AIChatbot({ open: openProp, onOpenChange }) {
    //     const controlled = openProp !== undefined;
    //     const { addWidget, state, updateWidget, updateLayout } = useReport();
    //     const schoolClasses = useClasses();
    //     const schoolStudents = useSchoolStudents({ sort: true });
    //     const [internalOpen, setInternalOpen] = useState(false);
    //     const isOpen = controlled ? openProp : internalOpen;
    //     const setIsOpen = controlled ? (v) => onOpenChange?.(typeof v === 'function' ? v(openProp) : v) : setInternalOpen;
    //     const [messages, setMessages] = useState([INITIAL_MESSAGE]);
    //     const [inputValue, setInputValue] = useState('');
    //     const [isTyping, setIsTyping] = useState(false);
    //     const [error, setError] = useState(null);
    //     const [pendingWidget, setPendingWidget] = useState(null);
    //     const [pendingEdit, setPendingEdit] = useState(null);
    //     const [widgetSelectionMatches, setWidgetSelectionMatches] = useState([]);
    //     const [currentPhase, setCurrentPhase] = useState('intent');
    //     const [pendingWidgetId, setPendingWidgetId] = useState(null);
    //     const pendingWidgetIdRef = useRef(null);
    //     const messagesEndRef = useRef(null);
    //     const inputRef = useRef(null);
    //     const [scopePickerOpen, setScopePickerOpen] = useState(false);
    //     const savedSlashRangeRef = useRef(null);
    //     const lastKnownRangeRef = useRef(null);
    //     const conversationIdRef = useRef(null);
    //     const exchangeIdRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping]);

    useEffect(() => {
        if (isOpen && composer.inputRef.current) {
            composer.inputRef.current.focus();
        }
    }, [isOpen]);

    const handleSuggestionClick = (prompt) => {
        if (composer.inputRef.current) {
            composer.inputRef.current.textContent = prompt;
        }
        composer.setInputValue(prompt);
        setTimeout(() => handleSendMessage({ overrideText: prompt }), 100);
    };

    const handleMessageSuggestionClick = (messageId, prompt) => {
        setMessages((prev) =>
            prev.map((msg) =>
                msg.id === messageId
                    ? {
                          ...msg,
                          suggestedPrompts: msg.suggestedPrompts?.filter((sp) => sp.prompt !== prompt),
                      }
                    : msg,
            ),
        );
        composer.setInputValue(prompt);
        setTimeout(() => handleSendMessage({ overrideText: prompt }), 100);
    };

    const handleKeyDownWithSend = (e) => {
        composer.handleKeyDown(e, () => handleSendMessage());
    };

    return (
        <>
            {!controlled && (
                <Tooltip title='Fae • Life Skills GO AI Agent' placement='right' arrow>
                    <Fab
                        size='medium'
                        aria-label='Fae - AI Report Designer'
                        onClick={() => {
                            if (!isOpen) {
                                posthog.capture(POSTHOG_EVENTS.FAE_CHATBOT_OPENED, {
                                    user_id: userId,
                                    school_id: schoolId,
                                });
                            }
                            setIsOpen(!isOpen);
                        }}
                        sx={{
                            position: 'fixed',
                            bottom: { xs: 16, sm: 24 },
                            left: { xs: 16, sm: 24 },
                            right: 'auto',
                            width: 56,
                            height: 56,
                            minHeight: 56,
                            background: headerGradient,
                            color: 'white',
                            boxShadow: '0 4px 16px rgba(13, 71, 161, 0.35)',
                            zIndex: 9999,
                            transform: 'scale(1)',
                            transition: 'transform 0.25s ease-out, box-shadow 0.25s ease, filter 0.25s ease',
                            '&:hover': {
                                background: headerGradient,
                                boxShadow: '0 8px 28px rgba(13, 71, 161, 0.5)',
                                filter: 'brightness(1.08)',
                                transform: 'scale(1.12)',
                            },
                        }}
                    >
                        <LightbulbOutlinedIcon sx={{ fontSize: 32 }} />
                    </Fab>
                </Tooltip>
            )}

            {isOpen && (
                <Paper
                    elevation={0}
                    className='ai-chatbot-panel'
                    sx={{
                        position: 'fixed',
                        top: 0,
                        bottom: 0,
                        right: 0,
                        left: 'auto',
                        width: { xs: '100%', sm: 440, md: 480 },
                        height: '100vh',
                        maxHeight: '100vh',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        borderRadius: '16px 0 0 16px',
                        boxShadow: '-8px 0 32px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)',
                        zIndex: 9999,
                        bgcolor: '#fff',
                    }}
                >
                    <Box
                        sx={{
                            py: 1.5,
                            px: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            background: headerGradient,
                            color: 'white',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                                variant='subtitle1'
                                fontWeight={600}
                                color='inherit'
                                fontFamily='var(--font-heading)'
                            >
                                Fae
                            </Typography>
                            <Typography variant='caption' sx={{ mt: 0.25, opacity: 0.95 }}>
                                • Life Skills GO AI Agent
                            </Typography>
                        </Box>
                        <IconButton
                            size='small'
                            onClick={() => setIsOpen(false)}
                            sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
                            aria-label='Close'
                        >
                            <CloseIcon fontSize='small' />
                        </IconButton>
                    </Box>

                    <Box
                        sx={{
                            px: 2,
                            py: 1,
                            bgcolor: 'rgba(13, 71, 161, 0.08)',
                            borderBottom: '1px solid rgba(13, 71, 161, 0.12)',
                            flexShrink: 0,
                        }}
                        aria-live='polite'
                    >
                        <Typography
                            variant='caption'
                            sx={{
                                display: 'block',
                                color: 'text.secondary',
                                fontSize: '0.7rem',
                                lineHeight: 1.4,
                            }}
                        >
                            {PII_DISCLAIMER}{' '}
                            <Link
                                href={PII_PROTECTION_HELP_URL}
                                target='_blank'
                                rel='noopener noreferrer'
                                underline='hover'
                                sx={{ fontSize: 'inherit', fontWeight: 500 }}
                            >
                                {PII_DISCLAIMER_LEARN_MORE}
                            </Link>
                        </Typography>
                        <Typography
                            variant='caption'
                            sx={{
                                display: 'block',
                                color: 'text.secondary',
                                fontSize: '0.7rem',
                                fontStyle: 'italic',
                                lineHeight: 1.4,
                                mt: 0.5,
                            }}
                        >
                            {AI_DISCLAIMER}
                        </Typography>
                    </Box>

                    <Box
                        id='aiChatbotMessages'
                        sx={{
                            flex: 1,
                            overflowY: 'auto',
                            p: 2,
                            bgcolor: '#fafafa',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                            minHeight: 0,
                            '&::-webkit-scrollbar': { width: 6 },
                            '&::-webkit-scrollbar-thumb': {
                                background: 'rgba(0,0,0,0.2)',
                                borderRadius: 3,
                            },
                        }}
                    >
                        {messages
                            .filter((m) => !m.isAutomaticProgression)
                            .map((message) => (
                                <ChatMessageBubble
                                    key={message.id}
                                    message={message}
                                    isTyping={isTyping}
                                    onSuggestionClick={handleMessageSuggestionClick}
                                    onLessonClick={openLessonPreview}
                                    schoolClasses={schoolClasses}
                                    schoolStudents={schoolStudents}
                                    userId={userId != null ? String(userId) : undefined}
                                    profile={profile}
                                />
                            ))}

                        {messages.length === 1 && (
                            <InitialSuggestions isTyping={isTyping} onSuggestionClick={handleSuggestionClick} />
                        )}

                        {isTyping && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    flexDirection: 'row',
                                    animation: 'fadeIn 0.25s ease-out',
                                    alignItems: 'flex-end',
                                }}
                            >
                                <Avatar sx={assistantAvatarSx}>
                                    <SmartToyIcon sx={{ fontSize: 18, color: 'white' }} />
                                </Avatar>
                                <Box
                                    sx={{
                                        bgcolor: '#fff',
                                        border: '1px solid rgba(0,0,0,0.08)',
                                        borderRadius: '4px 18px 18px 18px',
                                        p: 1.25,
                                        px: 1.5,
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                                    }}
                                >
                                    <Box
                                        className='ai-typing-indicator'
                                        sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                                    >
                                        <Typography component='span' variant='body2' color='text.secondary'>
                                            Thinking
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <Box className='ai-typing-dot' />
                                            <Box className='ai-typing-dot' />
                                            <Box className='ai-typing-dot' />
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {error && (
                            <Box sx={{ display: 'flex', gap: 1, flexDirection: 'row', alignItems: 'flex-end' }}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: 'var(--color-error)' }}>
                                    <WarningAmberIcon sx={{ fontSize: 18, color: 'white' }} />
                                </Avatar>
                                <Box
                                    sx={{
                                        bgcolor: '#fff',
                                        border: '1px solid var(--color-error)',
                                        borderRadius: '4px 18px 18px 18px',
                                        p: 1.25,
                                        color: 'var(--color-error)',
                                    }}
                                >
                                    {error}
                                </Box>
                            </Box>
                        )}

                        {widgetActionsWithSend.widgetSelectionMatches.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 1, flexDirection: 'row' }}>
                                <Avatar sx={assistantAvatarSx}>
                                    <SmartToyIcon sx={{ fontSize: 18, color: 'white' }} />
                                </Avatar>
                                <Paper
                                    variant='outlined'
                                    sx={{
                                        flex: 1,
                                        p: 1,
                                        borderRadius: 2,
                                        borderColor: 'rgba(0,0,0,0.08)',
                                        bgcolor: '#fff',
                                    }}
                                >
                                    <WidgetSelectionMenu
                                        widgets={widgetActionsWithSend.widgetSelectionMatches}
                                        onSelect={widgetActionsWithSend.handleWidgetSelect}
                                        onCancel={widgetActionsWithSend.handleWidgetSelectCancel}
                                    />
                                </Paper>
                            </Box>
                        )}

                        {editActions.pendingEdit && (
                            <Box sx={{ display: 'flex', gap: 1, flexDirection: 'row' }}>
                                <Avatar sx={assistantAvatarSx}>
                                    <SmartToyIcon sx={{ fontSize: 18, color: 'white' }} />
                                </Avatar>
                                <Paper
                                    variant='outlined'
                                    sx={{
                                        flex: 1,
                                        p: 1.5,
                                        borderRadius: 2,
                                        borderColor: 'rgba(0,0,0,0.08)',
                                        bgcolor: '#fff',
                                    }}
                                >
                                    <Box sx={{ mb: 1 }}>
                                        {editActions.pendingEdit.widget && (
                                            <>
                                                <Typography variant='body2' sx={{ mb: 0.5 }}>
                                                    I've prepared the following changes:
                                                </Typography>
                                                {(() => {
                                                    const lastEditMessage = [...messages].reverse().find((m) => m.edit);
                                                    const changes = lastEditMessage?.edit?.changes;
                                                    if (changes && Object.keys(changes).length > 0) {
                                                        return (
                                                            <Box sx={{ mb: 0.5, fontSize: '0.9rem' }}>
                                                                {Object.entries(changes).map(([key, value]) => (
                                                                    <Box key={key} sx={{ mb: 0.5 }}>
                                                                        <strong>{key}:</strong> {value}
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        );
                                                    }
                                                    return null;
                                                })()}
                                                <Typography variant='body2'>
                                                    Would you like to apply these changes?
                                                </Typography>
                                            </>
                                        )}
                                    </Box>
                                    <Button
                                        size='small'
                                        variant='contained'
                                        onClick={editActions.handleApplyEdit}
                                        sx={{
                                            background: headerGradient,
                                            color: '#fff',
                                            '&:hover': {
                                                filter: 'brightness(1.08)',
                                                background: headerGradient,
                                            },
                                        }}
                                    >
                                        Apply Changes
                                    </Button>
                                    <Button
                                        size='small'
                                        variant='outlined'
                                        onClick={editActions.handleDismissEdit}
                                        sx={{
                                            ml: 1,
                                            borderColor: 'rgba(0,0,0,0.23)',
                                            color: 'text.primary',
                                        }}
                                    >
                                        Dismiss
                                    </Button>
                                </Paper>
                            </Box>
                        )}

                        {widgetActionsWithSend.pendingWidget && (
                            <Box sx={{ display: 'flex', gap: 1, flexDirection: 'row' }}>
                                <Avatar sx={assistantAvatarSx}>
                                    <SmartToyIcon sx={{ fontSize: 18, color: 'white' }} />
                                </Avatar>
                                <Paper
                                    variant='outlined'
                                    sx={{
                                        flex: 1,
                                        p: 1.5,
                                        borderRadius: 2,
                                        borderColor: 'rgba(0,0,0,0.08)',
                                        bgcolor: '#fff',
                                    }}
                                >
                                    <Typography variant='body2' sx={{ mb: 1 }}>
                                        {widgetActionsWithSend.pendingWidget.widgets &&
                                        Array.isArray(widgetActionsWithSend.pendingWidget.widgets)
                                            ? `I've prepared ${widgetActionsWithSend.pendingWidget.widgets.length} panel${widgetActionsWithSend.pendingWidget.widgets.length > 1 ? 's' : ''} for you. Would you like to add ${widgetActionsWithSend.pendingWidget.widgets.length > 1 ? 'them' : 'it'} to your dashboard?`
                                            : "I've prepared an element for you. Would you like to add it to your dashboard?"}
                                    </Typography>
                                    <Button
                                        size='small'
                                        variant='contained'
                                        onClick={widgetActionsWithSend.handleAddWidget}
                                        sx={{
                                            background: headerGradient,
                                            color: '#fff',
                                            '&:hover': {
                                                filter: 'brightness(1.08)',
                                                background: headerGradient,
                                            },
                                        }}
                                    >
                                        {widgetActionsWithSend.pendingWidget.widgets &&
                                        Array.isArray(widgetActionsWithSend.pendingWidget.widgets)
                                            ? `Add ${widgetActionsWithSend.pendingWidget.widgets.length} Panel${widgetActionsWithSend.pendingWidget.widgets.length > 1 ? 's' : ''} to Dashboard`
                                            : 'Add Element to Dashboard'}
                                    </Button>
                                    <Button
                                        size='small'
                                        variant='outlined'
                                        onClick={widgetActionsWithSend.handleDismissWidget}
                                        sx={{
                                            ml: 1,
                                            borderColor: 'rgba(0,0,0,0.23)',
                                            color: 'text.primary',
                                        }}
                                    >
                                        Dismiss
                                    </Button>
                                </Paper>
                            </Box>
                        )}

                        <div ref={messagesEndRef} />
                    </Box>

                    <Box
                        sx={{
                            p: 1.5,
                            borderTop: '1px solid rgba(0,0,0,0.08)',
                            bgcolor: '#fff',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                        }}
                    >
                        <PiiResolutionCard
                            pendingPiiResolution={composer.pendingPiiResolution}
                            inputValue={composer.inputValue}
                            piiSearchQueries={composer.piiSearchQueries}
                            setPiiSearchQueries={composer.setPiiSearchQueries}
                            onReplace={composer.handlePiiReplace}
                            onDismiss={composer.handlePiiDismiss}
                            schoolStudents={schoolStudents}
                            schoolClasses={schoolClasses}
                        />
                        <ChatInputBar
                            inputRef={composer.inputRef}
                            inputValue={composer.inputValue}
                            isTyping={isTyping}
                            onInputSync={composer.handleInputSync}
                            onKeyDown={handleKeyDownWithSend}
                            onSendMessage={handleSendMessage}
                            onScopeSelect={composer.handleScopeSelect}
                            onScopeDismiss={composer.handleScopeDismiss}
                            scopePickerOpen={composer.scopePickerOpen}
                            onScopePickerOpenChange={composer.setScopePickerOpen}
                            existingScopeKeys={composer.existingScopeKeys}
                            schoolClasses={schoolClasses}
                            schoolStudents={schoolStudents}
                        />
                    </Box>
                </Paper>
            )}
        </>
    );
}
